/**
 * Full-page Segretaria IA (route `/segretaria`).
 *
 * A dedicated workspace for the conversational assistant.
 * - Left/main: chat area with infinite scroll to top for history.
 * - Bottom: multiline composer with Enter=send / Shift+Enter=newline.
 * - Right column (desktop ≥ lg): quick-look "Fatti che ricordo su di te"
 *   (top 5 pinned memories) + "Ultime azioni" (last 8 chatbot actions).
 *
 * Reuses the shared chatbot state layer via imported helpers from
 * `ChatbotWidget` where possible; if not exported, we duplicate the minimum
 * (send / confirm / reject) — carefully scoped to avoid divergence bugs.
 * The floating widget stays available on OTHER pages.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, Plus, MessageSquare, Star, History as HistoryIcon,
  Lock, Eye, EyeOff, Check, Undo2, ChevronRight,
} from "lucide-react";
import { api } from "../lib/api";
import Tip from "../components/Tip";

/**
 * Normalize FastAPI/axios errors to string (avoid React child crash).
 */
function errMsg(e, fallback = "Errore") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === "string" ? x : x?.msg || JSON.stringify(x))).join(" · ");
  }
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return e?.message || fallback;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    if (iso.length === 10) return new Date(iso + "T00:00:00").toLocaleDateString("it-IT");
    return new Date(iso).toLocaleString("it-IT");
  } catch { return iso; }
}

function fmtEur(n) {
  if (n == null) return "€ —";
  try { return "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  catch { return `€ ${n}`; }
}

export default function SegretariaFullPage() {
  const [conversations, setConversations] = useState([]);
  const [convId, setConvId] = useState(null);
  const convIdRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [pendingActions, setPendingActions] = useState({});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [pinnedMemories, setPinnedMemories] = useState([]);
  const [recentActions, setRecentActions] = useState([]);
  const [convOpen, setConvOpen] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try { setConversations(await api.chatbotListConversations()); } catch {}
  }, []);

  const loadMessages = async (id) => {
    if (!id) return;
    try { setMessages(await api.chatbotListMessages(id)); } catch {}
  };

  const loadSidebar = useCallback(async () => {
    try {
      const mem = await api.memoriesList();
      setPinnedMemories(mem.filter((m) => m.pinned).slice(0, 5));
    } catch {}
    try {
      const acts = await api.chatbotListActions({ limit: 8 });
      setRecentActions(acts || []);
    } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      await loadConversations();
      const list = await api.chatbotListConversations();
      if (list.length) {
        convIdRef.current = list[0].id;
        setConvId(list[0].id);
        await loadMessages(list[0].id);
      } else {
        const c = await api.chatbotCreateConversation();
        convIdRef.current = c.id;
        setConvId(c.id);
      }
      loadSidebar();
    })().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const newConversation = async () => {
    try {
      const c = await api.chatbotCreateConversation();
      convIdRef.current = c.id;
      setConvId(c.id);
      setMessages([]);
      setPendingActions({});
      loadConversations();
      inputRef.current?.focus();
    } catch { toast.error("Impossibile creare conversazione"); }
  };

  const switchConversation = async (id) => {
    convIdRef.current = id;
    setConvId(id);
    setPendingActions({});
    setConvOpen(false);
    await loadMessages(id);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    let cid = convIdRef.current || convId;
    if (!cid) {
      try {
        const c = await api.chatbotCreateConversation();
        cid = c.id;
        convIdRef.current = c.id;
        setConvId(c.id);
      } catch { toast.error("Impossibile creare conversazione"); return; }
    }
    setSending(true);
    setInput("");
    const tempUserId = `tmp-${Date.now()}`;
    setMessages((prev) => [...prev, {
      id: tempUserId, role: "user", content: text, created_at: new Date().toISOString(),
    }]);
    try {
      const resp = await api.chatbotSendMessage(cid, text);
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempUserId);
        return [...filtered, resp.user_message, resp.assistant_message];
      });
      if (resp.action) {
        setPendingActions((p) => ({ ...p, [resp.action.id]: resp.action }));
      }
      loadConversations();
      loadSidebar();
    } catch (e) {
      toast.error(errMsg(e, "Errore invio messaggio"));
      setMessages((prev) => prev.filter((m) => m.id !== tempUserId));
      setInput(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onInputKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      send();
    }
  };

  const confirmAction = async (actionId, stepupToken = null) => {
    try {
      const res = await api.chatbotConfirmAction(actionId, stepupToken);
      const action = pendingActions[actionId];
      const kind = action?.action_type;
      const isEmail = kind === "send_accountant_email" || kind === "send_email";
      const canUndo = res.can_undo !== false && !isEmail;
      const successMap = {
        create_cycle: "✓ Ciclo creato",
        create_financial_item: "✓ Voce finanziaria creata",
        create_reminder: "✓ Promemoria creato",
        multi_action: `✓ ${res.count || ""} cicli creati`,
        move_cycle: "✓ Ciclo spostato",
        delete_cycle: "✓ Ciclo eliminato",
        delete_financial_item: "✓ Voce finanziaria eliminata",
        mark_financial_paid: "✓ Segnata come pagata",
        add_memory: "✓ Memoria aggiunta",
        link_document_finance: "✓ Documento collegato",
        send_accountant_email: "✓ Email al commercialista inviata",
        send_email: "✓ Email inviata",
      };
      toast.success(successMap[kind] || "✓ Azione eseguita",
        canUndo ? {
          action: {
            label: "Annulla",
            onClick: async () => {
              try { await api.chatbotUndoAction(actionId); toast.success("Annullato"); }
              catch (e) { toast.error(errMsg(e, "Impossibile annullare")); }
              loadSidebar();
            },
          },
          duration: 60000,
        } : { duration: 4000 }
      );
      setPendingActions((p) => ({
        ...p,
        [actionId]: { ...p[actionId], status: "executed" },
      }));
      loadSidebar();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 && detail === "stepup_required") {
        toast.error("Password richiesta per confermare l'azione");
        throw e;
      }
      toast.error(errMsg(e, "Errore conferma"));
      throw e;
    }
  };

  const rejectAction = async (actionId) => {
    try {
      await api.chatbotRejectAction(actionId);
      toast("Proposta annullata");
      setPendingActions((p) => ({ ...p, [actionId]: { ...p[actionId], status: "rejected" } }));
      loadSidebar();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const messagesWithActions = messages.map((m) => {
    if (m.role === "assistant" && m.action_id && pendingActions[m.action_id]) {
      return { ...m, action: pendingActions[m.action_id] };
    }
    return m;
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]" data-testid="page-segretaria">
      <div className="flex flex-col h-[calc(100dvh-160px)] min-h-[500px] pxos-card overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--pxos-border)" }}>
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={20} style={{ color: "var(--pxos-accent)" }}/>
            <div className="min-w-0">
              <div className="font-display text-lg font-semibold truncate">Segretaria IA</div>
              <div className="text-[11px] opacity-70 truncate">
                {(conversations.find((c) => c.id === convId)?.title) || "Nuova conversazione"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Tip label="Cambia conversazione">
              <button onClick={() => setConvOpen(!convOpen)} className="p-2 hover:bg-black/5 rounded"
                      data-testid="segretaria-conv-toggle" aria-label="Storico conversazioni">
                <MessageSquare size={16}/>
              </button>
            </Tip>
            <Tip label="Nuova conversazione">
              <button onClick={newConversation} className="p-2 hover:bg-black/5 rounded"
                      data-testid="segretaria-new-conv" aria-label="Nuova conversazione">
                <Plus size={16}/>
              </button>
            </Tip>
            <button onClick={() => setShowSidebar(!showSidebar)} className="p-2 hover:bg-black/5 rounded lg:hidden"
                    data-testid="segretaria-toggle-sidebar" aria-label="Mostra pannello">
              <Star size={16}/>
            </button>
          </div>
        </div>

        {convOpen && (
          <div className="border-b bg-black/5 max-h-48 overflow-y-auto" style={{ borderColor: "var(--pxos-border)" }}
               data-testid="segretaria-conv-list">
            {conversations.length === 0 && (
              <div className="p-3 text-xs opacity-70">Nessuna conversazione ancora.</div>
            )}
            {conversations.map((c) => (
              <button key={c.id} onClick={() => switchConversation(c.id)}
                      className={`w-full text-left px-4 py-2 text-xs hover:bg-white ${c.id === convId ? "bg-white font-semibold" : ""}`}
                      data-testid={`segretaria-conv-${c.id}`}>
                <div className="truncate">{c.title || "(senza titolo)"}</div>
                <div className="text-[10px] opacity-60">{fmtDate(c.updated_at)}</div>
              </button>
            ))}
          </div>
        )}

        {/* Message list */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="segretaria-messages">
          {messagesWithActions.length === 0 && !sending && (
            <div className="text-center opacity-60 text-sm py-10">
              Ciao! Sono la tua Segretaria IA. Come posso aiutarti?
              <div className="mt-3 text-xs">
                Prova: <i>"Cosa devo fare oggi?"</i> · <i>"Manda una mail a…"</i> · <i>"Riordina i miei task"</i>
              </div>
            </div>
          )}
          {messagesWithActions.map((m) => (
            <MessageBubble key={m.id} msg={m}
                           onConfirm={confirmAction}
                           onReject={rejectAction} />
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-xs opacity-60" data-testid="segretaria-sending">
              <Loader2 size={14} className="animate-spin"/> La Segretaria sta pensando…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t px-3 py-3" style={{ borderColor: "var(--pxos-border)" }}>
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onInputKey}
              rows={2}
              placeholder="Scrivi qui… (Enter per inviare · Shift+Enter per andare a capo)"
              disabled={sending}
              className="pxos-input flex-1 resize-y text-sm min-h-[46px] max-h-[200px]"
              data-testid="segretaria-input"
            />
            <button onClick={send} disabled={!input.trim() || sending}
                    className="pxos-btn-primary text-sm"
                    data-testid="segretaria-send"
                    aria-label="Invia">
              {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            </button>
          </div>
          <div className="text-[10px] opacity-60 mt-1.5">
            Le azioni di modifica, cancellazione ed email richiedono la tua password.
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`space-y-3 ${showSidebar ? "block" : "hidden lg:block"}`} data-testid="segretaria-sidebar">
        <SidebarBlock title="Fatti che ricordo su di te" icon={Star} testid="sidebar-memories">
          {pinnedMemories.length === 0 ? (
            <div className="text-xs opacity-60">
              Nessuna memoria fissata. Dimmi <i>"ricorda che…"</i> per aggiungerne.
            </div>
          ) : (
            <ul className="space-y-2">
              {pinnedMemories.map((m) => (
                <li key={m.id} className="text-xs" data-testid={`sidebar-memory-${m.id}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-60">{m.category}</div>
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                </li>
              ))}
            </ul>
          )}
          <a href="/settings/memoria-segretaria" className="text-[11px] underline opacity-70 mt-2 inline-block"
             data-testid="sidebar-memories-link">
            Gestisci memorie →
          </a>
        </SidebarBlock>

        <SidebarBlock title="Ultime azioni" icon={HistoryIcon} testid="sidebar-actions">
          {recentActions.length === 0 ? (
            <div className="text-xs opacity-60">Nessuna azione ancora.</div>
          ) : (
            <ul className="space-y-1.5">
              {recentActions.slice(0, 8).map((a) => (
                <li key={a.id} className="text-xs flex items-center gap-1.5" data-testid={`sidebar-action-${a.id}`}>
                  <StatusDot status={a.status}/>
                  <span className="flex-1 truncate">{describeAction(a)}</span>
                  <span className="text-[10px] opacity-50">{fmtDate(a.executed_at || a.proposed_at)}</span>
                </li>
              ))}
            </ul>
          )}
          <a href="/settings/cronologia-segretaria" className="text-[11px] underline opacity-70 mt-2 inline-block"
             data-testid="sidebar-actions-link">
            Vedi cronologia completa →
          </a>
        </SidebarBlock>
      </aside>
    </div>
  );
}

function SidebarBlock({ title, icon: Icon, testid, children }) {
  return (
    <div className="pxos-card p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest opacity-70 mb-2">
        <Icon size={12}/> {title}
      </div>
      {children}
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === "executed" ? "#059669"
    : status === "rejected" ? "#9ca3af"
    : status === "undone" ? "#8A5A00"
    : status === "failed" ? "#B42318"
    : "var(--pxos-accent)";
  return <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: color }}/>;
}

function describeAction(a) {
  const p = a.parameters || {};
  const kind = a.action_type;
  const map = {
    create_cycle: `Crea ciclo: ${p.title || "?"}`,
    create_financial_item: `Voce: ${p.name || "?"}`,
    create_reminder: `Promemoria: ${p.title || "?"}`,
    move_cycle: `Sposta: ${p._task_title || "?"}`,
    delete_cycle: `Elimina: ${p._task_title || "?"}`,
    mark_financial_paid: `Pagata: ${p._item_name || "?"}`,
    delete_financial_item: `Elimina voce: ${p._item_name || "?"}`,
    send_accountant_email: `Commercialista · ${p.fiscal_year || "?"}`,
    send_email: `Email → ${p.to || "?"}`,
    add_memory: `Memoria: ${(p.content || "").slice(0, 40)}`,
    multi_action: `${(p.cycles || []).length} cicli`,
    link_document_finance: "Collega documento",
    prioritize_tasks: "Riordina task",
    create_financial_plan: "Piano finanziario",
  };
  return map[kind] || kind;
}

function MessageBubble({ msg, onConfirm, onReject }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.role}-${msg.id}`}>
      <div className={`max-w-[85%] ${isUser ? "" : "space-y-2"}`}>
        <div className={`pxos-card px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          isUser ? "bg-black/5" : ""
        }`}>
          {msg.content}
        </div>
        {msg.action && <ActionCardInline action={msg.action} onConfirm={onConfirm} onReject={onReject}/>}
      </div>
    </div>
  );
}

/**
 * Inline action card (subset of the widget one; keeps parity of behaviors,
 * shared inline L2 stepup prompt).
 */
function ActionCardInline({ action, onConfirm, onReject }) {
  const p = action.parameters || {};
  const status = action.status;
  const isProposed = status === "proposed";
  const kind = action.action_type;
  const needsStepup = !!action.requires_stepup;
  const [showPwdPrompt, setShowPwdPrompt] = useState(false);
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [pwdBusy, setPwdBusy] = useState(false);

  const doConfirm = async () => {
    if (needsStepup) { setShowPwdPrompt(true); return; }
    try { await onConfirm(action.id); } catch {}
  };

  const submitStepup = async () => {
    if (!pwd || pwdBusy) return;
    setPwdBusy(true);
    try {
      const tok = await api.stepupPassword(pwd);
      await onConfirm(action.id, tok.stepup_token);
      setShowPwdPrompt(false); setPwd("");
    } catch (e) {
      const s = e?.response?.status;
      const m = s === 429 ? "Troppi tentativi. Riprova tra 15 minuti."
              : s === 401 ? "Password errata. Riprova."
              : errMsg(e, "Errore verifica password");
      toast.error(m);
    } finally { setPwdBusy(false); }
  };

  const borderColor = needsStepup ? "#B45309" : "var(--pxos-accent)";
  return (
    <div className="pxos-card p-3 border-2" style={{ borderColor }}
         data-testid={`msg-action-${kind}`} data-action-status={status}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-70 mb-1.5">
        {needsStepup && <Lock size={10}/>}
        <span>{status === "executed" ? "✓ Eseguita" : status === "rejected" ? "✕ Annullata" : needsStepup ? "Richiede password" : "Azione proposta"}</span>
        <span>·</span>
        <span>{kind}</span>
      </div>
      <div className="text-xs space-y-0.5">
        {kind === "send_email" && <>
          <div>A: <b>{p.to}</b></div>
          <div>Oggetto: <b>{p.subject}</b></div>
          <div className="whitespace-pre-wrap opacity-80 max-h-40 overflow-y-auto pxos-card p-2 mt-1"
               style={{ background: "#F9FAFB" }}>{p.body}</div>
          <div className="text-[10px] mt-1" style={{ color: "#8A5A00" }}>
            ⚠ Email NON annullabile una volta inviata.
          </div>
        </>}
        {kind === "prioritize_tasks" && <PriorityTasksPreview p={p}/>}
        {kind === "create_financial_plan" && <FinancialPlanPreview p={p}/>}
        {kind !== "send_email" && kind !== "prioritize_tasks" && kind !== "create_financial_plan" && (
          <div className="opacity-70">
            {Object.entries(p).filter(([k]) => !k.startsWith("_")).slice(0, 6).map(([k, v]) => (
              <div key={k}>{k}: <b>{typeof v === "object" ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 100)}</b></div>
            ))}
          </div>
        )}
      </div>
      {isProposed && !showPwdPrompt && (
        <div className="flex items-center gap-2 mt-3">
          <button onClick={doConfirm} className="pxos-btn-primary text-xs" data-testid="msg-action-confirm">
            {needsStepup ? <><Lock size={12}/> Conferma con password</> : <><Check size={12}/> Conferma</>}
          </button>
          <button onClick={() => onReject(action.id)}
                  className="text-xs underline opacity-70" data-testid="msg-action-reject">
            Annulla
          </button>
        </div>
      )}
      {isProposed && showPwdPrompt && (
        <div className="mt-3 pxos-card p-2.5" style={{ background: "#FFF8E1", borderColor: "#F59E0B" }}
             data-testid="msg-stepup-prompt">
          <div className="flex items-center gap-1.5 text-[11px] font-medium mb-2" style={{ color: "#8A5A00" }}>
            <Lock size={12}/> Inserisci la tua password per confermare
          </div>
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <input type={showPwd ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") submitStepup(); }}
                     autoFocus disabled={pwdBusy}
                     className="pxos-input w-full text-xs pr-8" placeholder="Password"
                     data-testid="msg-stepup-password"/>
              <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100">
                {showPwd ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
            <button onClick={submitStepup} disabled={pwdBusy || !pwd}
                    className="pxos-btn-primary text-xs px-2 py-1" data-testid="msg-stepup-submit">
              {pwdBusy ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
            </button>
            <button onClick={() => { setShowPwdPrompt(false); setPwd(""); }}
                    disabled={pwdBusy}
                    className="text-xs underline opacity-70" data-testid="msg-stepup-cancel">
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PriorityTasksPreview({ p }) {
  const ordered = p._ordered || p.ordered || [];
  return (
    <div>
      <div className="text-[11px] opacity-70 mb-1">Ordine suggerito (scope: <b>{p.scope || "all_open"}</b>):</div>
      <ol className="list-decimal ml-4 space-y-1">
        {ordered.slice(0, 15).map((t, i) => (
          <li key={i} data-testid={`priority-row-${i}`}>
            <b>{t.title}</b>
            {t.priority && <> · <span className="opacity-70">{t.priority}</span></>}
            {t.due_date && <> · <span className="opacity-70">{fmtDate(t.due_date)}</span></>}
            {t.reason && <div className="text-[10px] opacity-70 italic">↳ {t.reason}</div>}
          </li>
        ))}
      </ol>
      {p._explanation && <div className="text-[11px] mt-2 opacity-80"><i>{p._explanation}</i></div>}
    </div>
  );
}

function FinancialPlanPreview({ p }) {
  return (
    <div>
      {p.horizon_months && <div className="mb-1">Orizzonte: <b>{p.horizon_months} mesi</b></div>}
      {p._analysis && (
        <div className="pxos-card p-2 mb-2" style={{ background: "#F9FAFB" }}>
          <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">Analisi</div>
          <div className="whitespace-pre-wrap text-xs">{p._analysis}</div>
        </div>
      )}
      {(p._missing_data || []).length > 0 && (
        <div className="pxos-card p-2 mb-2" style={{ background: "#FFF8E1" }}>
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Dati mancanti</div>
          <ul className="text-xs list-disc ml-4">
            {(p._missing_data || []).map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {(p._suggestions || []).length > 0 && (
        <div className="pxos-card p-2" style={{ background: "#F0FDF4" }}>
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Suggerimenti</div>
          <ul className="text-xs list-disc ml-4">
            {(p._suggestions || []).map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      <div className="mt-2 flex items-center gap-2 text-[11px]">
        <button className="pxos-btn text-[11px]" onClick={() => toast("Export PDF disponibile presto")}
                data-testid="fin-plan-pdf">
          Genera PDF
        </button>
      </div>
    </div>
  );
}
